/**
 * Site Banners - Display frequency and dismissal handling
 */
type DisplayFrequency = 'always' | 'once_per_session' | 'once_per_day';
type Trigger = 'on_load' | 'exit_intent';

interface BannerElement extends HTMLElement {
    dataset: DOMStringMap & {
        bannerId: string;
        displayFrequency?: DisplayFrequency;
        trigger?: Trigger;
    };
}

declare global {
    interface Window {
        dismissBanner: (bannerId: string) => void;
        Cookiebot?: { consent?: { marketing?: boolean } };
    }
}

const STORAGE_KEY_PREFIX = 'banner_dismissed_';
const SESSION_KEY_PREFIX = 'banner_session_';

function isDesktop(): boolean {
    return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
}

function hasMarketingConsent(): boolean {
    return window.Cookiebot?.consent?.marketing === true;
}

function shouldShowBanner(bannerId: string, frequency: DisplayFrequency): boolean {
    switch (frequency) {
        case 'always':
            return true;

        case 'once_per_session':
            return !sessionStorage.getItem(SESSION_KEY_PREFIX + bannerId);

        case 'once_per_day': {
            const lastShown = localStorage.getItem(STORAGE_KEY_PREFIX + bannerId);

            if (!lastShown) {
                return true;
            }

            const lastShownDate = new Date(parseInt(lastShown));
            const today = new Date();

            return lastShownDate.toDateString() !== today.toDateString();
        }

        default:
            return true;
    }
}

function markBannerAsShown(bannerId: string, frequency: DisplayFrequency): void {
    switch (frequency) {
        case 'once_per_session':
            sessionStorage.setItem(SESSION_KEY_PREFIX + bannerId, '1');
            break;

        case 'once_per_day':
            localStorage.setItem(STORAGE_KEY_PREFIX + bannerId, Date.now().toString());
            break;
    }
}

function dismissBanner(bannerId: string): void {
    const banner = document.getElementById('banner-' + bannerId) as BannerElement | null;

    if (!banner) {
        return;
    }

    const frequency = (banner.dataset.displayFrequency || 'always') as DisplayFrequency;

    markBannerAsShown(bannerId, frequency);
    banner.classList.add('hiding');

    setTimeout(() => {
        banner.style.display = 'none';
        banner.classList.remove('hiding');

        // Removing a top banner shifts the navbar up; notify listeners (e.g. the
        // open mobile menu re-anchors itself to the navbar's new bottom edge).
        window.dispatchEvent(new Event('resize'));

        // For bottom banners, adjust body padding
        if (banner.classList.contains('site-banner--bottom')) {
            document.body.style.paddingBottom = '0';
        }

        // For popup banners, restore body scrolling
        if (banner.classList.contains('site-banner-popup')) {
            document.documentElement.style.overflow = '';
            document.body.style.overflow = '';
        }
    }, 300);
}

function openPopup(bannerEl: BannerElement, frequency: DisplayFrequency): void {
    bannerEl.style.display = 'flex';
    markBannerAsShown(bannerEl.dataset.bannerId, frequency);
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
}

const exitIntentPopups: BannerElement[] = [];
let exitIntentArmed = false;

function onExitIntent(e: MouseEvent): void {
    if (e.relatedTarget || e.clientY > 0 || !hasMarketingConsent()) {
        return;
    }

    // Stay armed while another popup is open, so a later exit can still trigger.
    const anyPopupOpen = Array.from(document.querySelectorAll<HTMLElement>('.site-banner-popup'))
        .some((el) => el.style.display !== 'none');

    if (anyPopupOpen) {
        return;
    }

    document.removeEventListener('mouseout', onExitIntent);

    // One exit-intent popup per page view; frequency is re-checked in case another tab already showed it.
    const popup = exitIntentPopups.find((el) =>
        shouldShowBanner(el.dataset.bannerId, (el.dataset.displayFrequency || 'always') as DisplayFrequency),
    );

    if (popup) {
        openPopup(popup, (popup.dataset.displayFrequency || 'always') as DisplayFrequency);
    }
}

function armExitIntent(): void {
    if (exitIntentArmed || !exitIntentPopups.length || !isDesktop() || !hasMarketingConsent()) {
        return;
    }

    exitIntentArmed = true;
    document.addEventListener('mouseout', onExitIntent);
}

function showBanner(banner: Element): void {
    const bannerEl = banner as BannerElement;
    const bannerId = bannerEl.dataset.bannerId;
    const frequency = (bannerEl.dataset.displayFrequency || 'always') as DisplayFrequency;
    const isPopup = bannerEl.classList.contains('site-banner-popup');
    const isBottom = bannerEl.classList.contains('site-banner--bottom');

    if (!shouldShowBanner(bannerId, frequency)) {
        if (!isPopup) {
            bannerEl.style.display = 'none';
        }
        return;
    }

    if (isPopup && bannerEl.dataset.trigger === 'exit_intent') {
        exitIntentPopups.push(bannerEl);
    } else if (isPopup) {
        setTimeout(() => openPopup(bannerEl, frequency), 1000);
    } else {
        bannerEl.style.display = 'block';
        markBannerAsShown(bannerId, frequency);

        if (isBottom) {
            document.body.style.paddingBottom = bannerEl.offsetHeight + 'px';
        }
    }
}

function initBanners(): void {
    document.querySelectorAll('.site-banner, .site-banner-popup').forEach(showBanner);
    armExitIntent();
    window.addEventListener('CookiebotOnAccept', armExitIntent);

    document.addEventListener('keydown', (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
            document.querySelectorAll<BannerElement>('.site-banner-popup').forEach((popup) => {
                if (popup.style.display !== 'none') {
                    dismissBanner(popup.dataset.bannerId);
                }
            });
        }
    });
}

// Expose dismissBanner globally for onclick handlers
window.dismissBanner = dismissBanner;

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initBanners);
} else {
    initBanners();
}

export { dismissBanner, initBanners };
